import React from 'react';
import { MapNode } from './useMapLayout';

interface Props {
  node: MapNode;
  transform: { x: number; y: number; scale: number };
  canvasWidth: number;
  canvasHeight: number;
  darkMode: boolean;
  needsRecompile: boolean;
}

const SystemPromptOverlay: React.FC<Props> = ({
  node, transform, canvasWidth, canvasHeight, darkMode, needsRecompile,
}) => {
  const toScreen = (wx: number, wy: number) => ({
    sx: wx * transform.scale + transform.x,
    sy: wy * transform.scale + transform.y,
  });

  const { sx: cx, sy: cy } = toScreen(node.x, node.y);
  const screenR = node.radius * transform.scale;

  if (cx + screenR < 0 || cx - screenR > canvasWidth ||
      cy + screenR < 0 || cy - screenR > canvasHeight) return null;

  const prompt: string = node.data || '';
  const tokenEstimate = Math.round(prompt.length / 4);
  const wordCount = prompt.trim().split(/\s+/).filter(Boolean).length;
  const lineCount = prompt.split('\n').length;
  const preview = prompt.length > 320 ? prompt.slice(0, 320) + '…' : prompt;
  const cardW = Math.max(200, Math.min(320, screenR * 2.2));

  return (
    <div
      style={{
        position: 'absolute',
        left: cx - cardW / 2,
        top: cy - screenR - 8,
        width: cardW,
        transform: 'translateY(-100%)',
        pointerEvents: 'none',
        zIndex: 30,
      }}
    >
      <div className={`rounded-xl border shadow-2xl overflow-hidden ${
        needsRecompile
          ? darkMode ? 'bg-amber-950/90 border-amber-600' : 'bg-amber-50 border-amber-400'
          : darkMode ? 'bg-gray-900/95 border-green-700' : 'bg-white border-green-400'
      }`}>
        {/* Header */}
        <div className={`px-3 py-1.5 flex items-center gap-2 border-b ${
          needsRecompile
            ? darkMode ? 'bg-amber-900/60 border-amber-700' : 'bg-amber-100 border-amber-300'
            : darkMode ? 'bg-green-900/40 border-green-800' : 'bg-green-50 border-green-200'
        }`}>
          <span style={{ fontSize: 10 }}>⬡</span>
          <span className={`font-bold uppercase tracking-wide ${
            needsRecompile
              ? darkMode ? 'text-amber-300' : 'text-amber-700'
              : darkMode ? 'text-green-300' : 'text-green-700'
          }`} style={{ fontSize: 9 }}>
            {needsRecompile ? '⚠ Stale — System Prompt' : '✓ System Prompt'}
          </span>
        </div>

        {/* Stats strip */}
        <div className={`flex items-center gap-3 px-3 py-1 border-b ${
          darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-100 text-gray-500'
        }`} style={{ fontSize: 9 }}>
          <span>~{tokenEstimate.toLocaleString()} tokens</span>
          <span>{wordCount.toLocaleString()} words</span>
          <span>{lineCount} lines</span>
        </div>

        {/* Prompt preview */}
        <div className={`px-3 py-2 font-mono leading-relaxed ${
          darkMode ? 'text-gray-300' : 'text-gray-700'
        }`} style={{ fontSize: 8, whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'hidden' }}>
          {preview || <span className="italic opacity-50">Not compiled yet</span>}
        </div>
      </div>
    </div>
  );
};

export default SystemPromptOverlay;
